import { max, min, range, sortBy } from 'lodash-es';
import { DateTime } from 'luxon';
import * as React from 'react';
import {
    DocumentUserPlanningFragment,
    EventsDocumentInput,
    PsuiUserPlanningFragment,
    UserPlanningDocumentOptions
} from '../../generated/types';
import { TemplateService } from '../../services/templateService';
import { isEmptyArray } from '../../util/array';
import { useService } from '../../util/dependencies/dependencies';
import { filterStartEndDates } from '../../vo/positionSlotUserInfo';
import { UserPlanningContainer } from './userPlanningContainer';
import { UserPlanningPage } from './userPlanningPage';

interface IUserPlanningComponentProps {
    assignments: PsuiUserPlanningFragment[];
    eventDocument: EventsDocumentInput;
    event: DocumentUserPlanningFragment;
    options: UserPlanningDocumentOptions;

    renderText(text: string): string;
}

const UserPlanningComponent = (props: IUserPlanningComponentProps) => {
    const { minDate, numberOfWeeks } = React.useMemo(() => {
        const minDate = min(
            props.assignments.map((psui) => psui.positionSlot.range.start!)
        )!.startOf('week');
        const maxDate = max(props.assignments.map((psui) => psui.positionSlot.range.end!))!.endOf(
            'week'
        );

        return { minDate, numberOfWeeks: Math.ceil(maxDate.diff(minDate, 'weeks').weeks) };
    }, [props.assignments]);

    return (
        <>
            {range(0, numberOfWeeks).map((i) => (
                <UserPlanningPage
                    key={i}
                    assignments={props.assignments}
                    document={props.eventDocument}
                    event={props.event}
                    options={props.options}
                    renderText={props.renderText}
                    startDate={minDate.plus({ week: i })}
                />
            ))}
        </>
    );
};

interface IUserPlanningProps {
    date?: DateTime;
    endDate?: DateTime;
    event: DocumentUserPlanningFragment;
    eventDocument: EventsDocumentInput;
    options: UserPlanningDocumentOptions;
}

export const UserPlanning = (props: IUserPlanningProps) => {
    const templateService = useService(TemplateService);
    const assignments = React.useMemo(
        () =>
            sortBy(
                filterStartEndDates(
                    props.event.volunteerRegistration.positionsSlotsUsersInfos,
                    props.date,
                    props.endDate
                ),
                (psui) => psui.positionSlot.range.start!.toMillis()
            ),
        [props.event]
    );
    const renderText = (text: string) =>
        templateService.renderText(text, {
            userInfo: props.event.volunteerRegistration.userInfo,
            volunteerRegistration: props.event.volunteerRegistration
        });

    if (isEmptyArray(assignments)) {
        return (
            <UserPlanningContainer
                document={props.eventDocument}
                event={props.event}
                options={props.options}
                renderText={renderText}
            />
        );
    } else {
        return (
            <UserPlanningComponent
                assignments={assignments}
                event={props.event}
                eventDocument={props.eventDocument}
                options={props.options}
                renderText={renderText}
            />
        );
    }
};
